<template>
   <div class="maintenance-settings-page">

      <!-- ── Page Header ── -->
      <div class="ms-page-header">
         <div class="ms-page-header__content">
            <div class="ms-page-header__icon">
               <i class="fas fa-cogs"></i>
            </div>
            <div>
               <p class="ms-page-header__breadcrumb">Platform Settings <span class="ms-breadcrumb-sep">/</span> Maintenance Settings</p>
               <h1 class="ms-page-header__title">Maintenance Settings</h1>
               <p class="ms-page-header__subtitle">Manage groups, categories, and subcategories</p>
            </div>
         </div>
      </div>

      <!-- ── Summary Cards ── -->
      <div class="ms-summary-row">
         <div class="ms-stat-card">
            <div class="ms-stat-icon" style="background: linear-gradient(135deg, #00A0C1, #33b5d1);">
               <i class="fas fa-layer-group"></i>
            </div>
            <div class="ms-stat-body">
               <span class="ms-stat-value">{{ pagination.totalItems }}</span>
               <span class="ms-stat-label">Groups</span>
            </div>
         </div>
         <div class="ms-stat-card">
            <div class="ms-stat-icon" style="background: linear-gradient(135deg, #f59e0b, #fbbf24);">
               <i class="fas fa-folder-open"></i>
            </div>
            <div class="ms-stat-body">
               <span class="ms-stat-value">{{ totalCategories }}</span>
               <span class="ms-stat-label">Categories</span>
            </div>
         </div>
         <div class="ms-stat-card">
            <div class="ms-stat-icon" style="background: linear-gradient(135deg, #10b981, #34d399);">
               <i class="fas fa-tags"></i>
            </div>
            <div class="ms-stat-body">
               <span class="ms-stat-value">{{ totalSubcategories }}</span>
               <span class="ms-stat-label">Subcategories</span>
            </div>
         </div>
      </div>

      <!-- ── Main Table Card ── -->
      <div class="ms-card">
         <div class="ms-card__toolbar">
            <button v-if="$store.getters['auth/isGlobalSuperAdmin']" type="button" class="ms-btn ms-btn--primary"
               data-bs-toggle="modal" data-bs-target="#groupForm" @click="groupModalAction('create')">
               <i class="fas fa-plus"></i> Create Group
            </button>
            <div class="ms-pagination">
               <button @click="previousPage()" :disabled="pagination.page === 1" class="ms-page-btn">
                  <i class="fas fa-chevron-left"></i>
               </button>
               <span class="ms-page-label">
                  <strong>{{ paginationLabel }}</strong> of {{ pagination.totalItems }}
               </span>
               <button @click="nextPage()"
                  :disabled="pagination.page === pagination.totalPages || pagination.totalItems === 0"
                  class="ms-page-btn">
                  <i class="fas fa-chevron-right"></i>
               </button>
            </div>
         </div>

         <div class="ms-table-wrap">
            <table class="ms-table" id="accordion-group">
               <thead>
                  <tr>
                     <th v-for="header in filteredHeaders" :key="header.value">{{ header.text }}</th>
                  </tr>
               </thead>

               <tbody v-if="pagination.totalItems <= 0">
                  <tr>
                     <td :colspan="filteredHeaders.length" class="ms-empty-row">
                        <div class="ms-empty-state">
                           <i class="fas fa-inbox"></i>
                           <span>No records to display.</span>
                        </div>
                     </td>
                  </tr>
               </tbody>

               <tbody v-for="(group, index) in groups" :key="group.id">
                  <tr class="ms-group-row">
                     <td class="ms-cell-name">
                        <button class="ms-expand-btn" data-bs-toggle="collapse" :data-bs-target="'#collapse'+index"
                           @click="collapseMainRow()">
                           <i class="fas fa-chevron-right"></i>
                        </button>
                        <span class="ms-group-name">{{ group.display_name }}</span>
                     </td>
                     <td>
                        <span class="ms-count-badge ms-count-badge--category">{{ group.total_category }}</span>
                     </td>
                     <td>
                        <span class="ms-count-badge ms-count-badge--subcategory">{{ group.total_subcategory }}</span>
                     </td>
                     <td v-if="$store.getters['auth/isGlobalSuperAdmin']" class="ms-actions">
                        <button type="button" class="ms-action-btn ms-action-btn--edit" data-bs-toggle="modal" data-bs-target="#groupForm"
                           @click="groupModalAction('edit', group.id)" title="Edit Group">
                           <i class="fa fa-pen"></i>
                        </button>

                        <span v-if="group.is_used" title="Cannot delete group currently being used.">
                           <button type="button" class="ms-action-btn ms-action-btn--delete ms-action-btn--disabled"
                              @click="groupModalAction('delete', group.id)" :disabled="group.is_used">
                              <i class="fa fa-xmark"></i>
                           </button>
                        </span>
                        <span v-else>
                           <button type="button" class="ms-action-btn ms-action-btn--delete"
                              @click="groupModalAction('delete', group.id)" :disabled="group.is_used">
                              <i class="fa fa-xmark"></i>
                           </button>
                        </span>
                     </td>
                  </tr>

                  <!-- SUB TABLE — CATEGORIES -->
                  <tr v-if="group.length != 0" class="ms-subtable-row collapse" :id="'collapse'+index" data-bs-parent="#accordion-group">
                     <td :colspan="filteredHeaders.length" class="ms-subtable-cell">
                        <Category :index="index" :items="group" :mainRowCollapsed="isMainRowCollapsed"
                           @onButtonClick="categoryModalAction" @onSubcategoryButtonClick="subcategoryModalAction" />
                     </td>
                  </tr>
               </tbody>
            </table>
         </div>
      </div>
      
   </div>

   <!-- ========== GROUP FORM MODAL ========== -->
   <div class="modal fade" id="groupForm" tabindex="-1" data-bs-keyboard="false" data-bs-backdrop="static" ref="groupForm">
      <div ref="groupModalDialogRef" class="modal-dialog modal-dialog-scrollable">
         <div class="modal-content">
            <div class="modal-header justify-content-end">
               <div>
                  <button ref="closeGroupModalActionForm" type="button" class="btn-close" data-bs-dismiss="modal"
                     aria-label="Close" @click="this.$refs.groupFormRef.resetForm()"></button>
               </div>
            </div>
            <div class="modal-body">
               <GroupForm ref="groupFormRef" @onSubmit="init"/>
            </div>
         </div>
      </div>
   </div>

   <!-- ========== CATEGORY FORM MODAL ========== -->
   <div class="modal fade" id="categoryForm" tabindex="-1" data-bs-keyboard="false" data-bs-backdrop="static" ref="categoryForm">
      <div ref="categoryModalDialogRef" class="modal-dialog modal-dialog-scrollable">
         <div class="modal-content">
            <div class="modal-header justify-content-end">
               <div>
                  <button ref="closeCategoryModalActionForm" type="button" class="btn-close" data-bs-dismiss="modal"
                     aria-label="Close" @click="this.$refs.categoryFormRef.resetForm()"></button>
               </div>
            </div>
            <div class="modal-body">
               <CategoryForm ref="categoryFormRef" @onSubmit="init"/>
            </div>
         </div>
      </div>
   </div>

   <!-- ========== SUBCATEGORY FORM MODAL ========== -->
   <div class="modal fade" id="subcategoryForm" tabindex="-1" data-bs-keyboard="false" data-bs-backdrop="static" ref="subcategoryForm">
      <div ref="subcategoryModalDialogRef" class="modal-dialog modal-dialog-scrollable">
         <div class="modal-content">
            <div class="modal-header justify-content-end">
               <div>
                  <button ref="closeSubcategoryModalActionForm" type="button" class="btn-close" data-bs-dismiss="modal"
                     aria-label="Close" @click="this.$refs.subcategoryFormRef.resetForm()"></button>
               </div>
            </div>
            <div class="modal-body">
               <SubcategoryForm ref="subcategoryFormRef" @onSubmit="init"/>
            </div>
         </div>
      </div>
   </div>

</template>

<script>
import  Category  from '@/components/MaintenanceSettings/Category.vue';
import  GroupForm  from '@/components/MaintenanceSettings/GroupForm.vue';
import  CategoryForm  from '@/components/MaintenanceSettings/CategoryForm.vue';
import  SubcategoryForm  from '@/components/MaintenanceSettings/SubcategoryForm.vue';


export default ({

   components: {
      Category, 
      GroupForm,
      CategoryForm,
      SubcategoryForm
   },
   
   data(){
      return{
         headers: [
            { text: "Group Name", value: "groupName" },
            { text: "No. of Categories", value: "numberOfCategories" },
            { text: "No. of Subcategories", value: "numberOfSubcategories" },
            { text: "Action", value: "action" },
         ],
         groups: [],
         isMainRowCollapsed: true,
         pagination: {
            pageSize: 10,
            page: 1,
            totalItems: 0,
            totalPages: 1,
         },
         
      }
         
   },

   created() {
      this.getGroupsList()
   },

   mounted() {
      document.addEventListener('click', this.handleClickOutsideGroup);
      document.addEventListener('click', this.handleClickOutsideCategory);
      document.addEventListener('click', this.handleClickOutsideSubCategory);
   },

   beforeUnmount() {
      document.removeEventListener('click', this.handleClickOutsideGroup);
      document.removeEventListener('click', this.handleClickOutsideCategory);
      document.removeEventListener('click', this.handleClickOutsideSubCategory);
   },


   computed: {
      paginationLabel() {
         var start =  Math.min((this.pagination.page - 1) * this.pagination.pageSize + 1, this.pagination.totalItems);
         var end = Math.min((this.pagination.page * this.pagination.pageSize), this.pagination.totalItems)
         
         return start + " - " + end
      },

      currentPage() {
         return this.pagination.page
      },

      totalCategories() {
         return this.groups.reduce((sum, g) => sum + (g.total_category || 0), 0);
      },

      totalSubcategories() {
         return this.groups.reduce((sum, g) => sum + (g.total_subcategory || 0), 0);
      },

      filteredHeaders() {
         if (!this.$store.getters['auth/isGlobalSuperAdmin']) {
            return this.headers.filter(h => h.value !== 'action');
         }
         return this.headers;
      },

   },

   watch: {
      currentPage() {
         this.getGroupsList()
      }

   },

   methods: {

      handleClickOutsideGroup(event) {
         if (
            this.$refs.groupModalDialogRef &&
            !this.$refs.groupModalDialogRef.contains(event.target) &&
            this.$refs.groupForm.classList.contains('show')
         ) {
            const closeButton = this.$refs.closeGroupModalActionForm;
            if (closeButton) {
               closeButton.click();
            }
         }
      },

      handleClickOutsideCategory(event) {
         if (
            this.$refs.categoryModalDialogRef &&
            !this.$refs.categoryModalDialogRef.contains(event.target) &&
            this.$refs.categoryForm.classList.contains('show')
         ) {
            const closeButton = this.$refs.closeCategoryModalActionForm;
            if (closeButton) {
               closeButton.click();
            }
         }
      },

      handleClickOutsideSubCategory(event) {
         if (
            this.$refs.subcategoryModalDialogRef &&
            !this.$refs.subcategoryModalDialogRef.contains(event.target) &&
            this.$refs.subcategoryForm.classList.contains('show')
         ) {
            const closeButton = this.$refs.closeSubcategoryModalActionForm;
            if (closeButton) {
               closeButton.click();
            }
         }
      },

      init(){
         this.getGroupsList()
      },

      groupModalAction(action, id){
         if(action=="create"){
            this.$refs.groupFormRef.isCreate()
         } else if(action=="edit"){
            this.$refs.groupFormRef.isEdit(id)
         } else if(action=="delete"){
            this.$refs.groupFormRef.isDelete(id)
         }
      },

      categoryModalAction(action, id){
         if(action=="create"){
            this.$refs.categoryFormRef.isCreate(id)
         } else if(action=="edit"){
            this.$refs.categoryFormRef.isEdit(id)
         } else if(action=="delete"){
            this.$refs.categoryFormRef.isDelete(id)
         }
      },

      subcategoryModalAction(action, id, groupId){
         if(action=="create"){
            this.$refs.subcategoryFormRef.isCreate(id, groupId)
         } else if(action=="edit"){
            this.$refs.subcategoryFormRef.isEdit(id, groupId)
         } else if(action=="delete"){
            this.$refs.subcategoryFormRef.isDelete(id)
         }
      },

      collapseMainRow(){
         this.isMainRowCollapsed = !this.isMainRowCollapsed
      },

      previousPage(){
         this.pagination.page > 1 ? this.pagination.page -= 1 : ''
      },

      nextPage() {
         this.pagination.totalPages > this.pagination.page ? this.pagination.page += 1 : ''
      },

      async getGroupsList() {
         await this.$axios.get("/groups?pageSize="+this.pagination.pageSize+"&page="+this.pagination.page)
         .then((response) => {
            this.groups = response.data.groups
            this.pagination.totalItems = response.data.total_items
            this.pagination.totalPages = Math.ceil(this.pagination.totalItems / this.pagination.pageSize)
         })
         .catch((error) => {
            this.$toast.error(error.response.data.message)
         })
      },
      
   }
   
})
</script>

<style scoped>
/* ═══════════════════════════════════════════════════════════
   MAINTENANCE SETTINGS — Premium Redesign (Vertex Blue)
   ═══════════════════════════════════════════════════════════ */

/* ── Page Header ── */
.ms-page-header {
   margin-bottom: 24px;
   padding: 20px 24px;
   background: linear-gradient(135deg, #ffffff 0%, #f4f9fb 100%);
   border: 1px solid rgba(0, 130, 152, 0.15);
   border-left: 5px solid #008298;
   border-radius: 12px;
   box-shadow: 0 4px 18px rgba(0, 78, 90, 0.05);
}

.ms-page-header__content {
   display: flex;
   align-items: center;
   gap: 16px;
}

.ms-page-header__icon {
   width: 44px;
   height: 44px;
   border-radius: 10px;
   background: rgba(0, 130, 152, 0.08);
   display: flex;
   align-items: center;
   justify-content: center;
   font-size: 1.15rem;
   color: #008298;
}

.ms-page-header__title {
   font-size: 1.45rem;
   font-weight: 800;
   color: #00363f;
   margin: 0;
   letter-spacing: -0.3px;
}

.ms-page-header__subtitle {
   font-size: 0.85rem;
   color: #557d84;
   margin: 4px 0 0;
   font-weight: 400;
}

.ms-page-header__breadcrumb {
   font-size: 0.76rem;
   color: #557d84;
   font-weight: 500;
   margin: 0 0 4px;
   letter-spacing: 0.3px;
}

.ms-breadcrumb-sep {
   margin: 0 6px;
   color: rgba(0, 130, 152, 0.3);
}

/* ── Summary Cards ── */
.ms-summary-row {
   display: grid;
   grid-template-columns: repeat(3, 1fr);
   gap: 16px;
   margin-bottom: 20px;
}

.ms-stat-card {
   background: #fff;
   border-radius: 14px;
   padding: 20px;
   display: flex;
   align-items: center;
   gap: 16px;
   box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04);
   transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.ms-stat-card:hover {
   transform: translateY(-2px);
   box-shadow: 0 8px 24px rgba(0, 0, 0, 0.08);
}

.ms-stat-icon {
   width: 48px;
   height: 48px;
   border-radius: 12px;
   display: flex;
   align-items: center;
   justify-content: center;
   color: #fff;
   font-size: 1.15rem;
   flex-shrink: 0;
}

.ms-stat-body {
   display: flex;
   flex-direction: column;
}

.ms-stat-value {
   font-size: 1.6rem;
   font-weight: 700;
   color: #1e293b;
   line-height: 1.2;
}

.ms-stat-label {
   font-size: 0.8rem;
   color: #94a3b8;
   font-weight: 500;
   text-transform: uppercase;
   letter-spacing: 0.5px;
   margin-top: 2px;
}

/* ── Main Card ── */
.ms-card {
   background: #fff;
   border-radius: 16px;
   box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04);
   overflow: visible;
}

/* ── Toolbar ── */
.ms-card__toolbar {
   display: flex;
   align-items: center;
   justify-content: space-between;
   flex-wrap: wrap;
   gap: 12px;
   padding: 16px 20px;
   border-bottom: 1px solid #f1f5f9;
}

/* ── Pagination ── */
.ms-pagination {
   display: inline-flex;
   align-items: center;
   border: 1.5px solid #e2e8f0;
   border-radius: 10px;
   overflow: visible;
   background: #f8fafc;
}

.ms-page-btn {
   background: transparent;
   border: none;
   cursor: pointer;
   color: #00A0C1;
   font-size: 0.85rem;
   padding: 7px 12px;
   transition: background-color 0.15s;
}

.ms-page-btn:hover:not(:disabled) {
   background: #e6f7fa;
}

.ms-page-btn:disabled {
   color: #cbd5e1;
   cursor: not-allowed;
}

.ms-page-label {
   font-size: 0.82rem;
   color: #64748b;
   font-weight: 500;
   padding: 0 6px;
   white-space: nowrap;
}

.ms-page-label strong {
   color: #1e293b;
}

/* ── Buttons ── */
.ms-btn {
   padding: 9px 20px;
   border-radius: 10px;
   font-size: 0.875rem;
   font-weight: 600;
   cursor: pointer;
   display: inline-flex;
   align-items: center;
   gap: 6px;
   transition: all 0.2s ease;
   border: none;
}

.ms-btn--primary {
   background: linear-gradient(135deg, #00A0C1, #33b5d1);
   color: #fff;
   box-shadow: 0 2px 8px rgba(0, 160, 193, 0.3);
}

.ms-btn--primary:hover {
   background: linear-gradient(135deg, #008da8, #00A0C1);
   box-shadow: 0 4px 16px rgba(0, 160, 193, 0.4);
   transform: translateY(-1px);
}

/* ── Table ── */
.ms-table-wrap {
   overflow-x: auto;
}

.ms-table {
   width: 100%;
   border-collapse: collapse;
}

.ms-table thead tr {
   background: linear-gradient(135deg, #f8fafc 0%, #e6f7fa 100%);
}

.ms-table th {
   padding: 13px 16px;
   text-align: left;
   font-size: 0.75rem;
   font-weight: 700;
   color: #475569;
   text-transform: uppercase;
   letter-spacing: 0.6px;
   border-bottom: 2px solid #e2e8f0;
   white-space: nowrap;
}

.ms-table td {
   padding: 14px 16px;
   font-size: 0.875rem;
   color: #334155;
   border-bottom: 1px solid #f1f5f9;
   vertical-align: middle;
}

.ms-table tbody tr {
   transition: background-color 0.15s ease;
}

.ms-group-row:hover {
   background-color: #f5fcfd;
}

/* ── Group Row ── */
.ms-cell-name {
   display: flex;
   align-items: center;
   gap: 8px;
}

.ms-expand-btn {
   background: none;
   border: none;
   cursor: pointer;
   color: #00A0C1;
   font-size: 0.7rem;
   width: 28px;
   height: 28px;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   border-radius: 8px;
   transition: background-color 0.15s, transform 0.2s;
   flex-shrink: 0;
}

.ms-expand-btn:hover {
   background: #e6f7fa;
}

.ms-expand-btn i {
   transition: transform 0.2s ease;
}

.ms-group-name {
   font-weight: 600;
   color: #1e293b;
}

/* ── Count Badges ── */
.ms-count-badge {
   display: inline-flex;
   align-items: center;
   justify-content: center;
   min-width: 28px;
   padding: 3px 10px;
   border-radius: 16px;
   font-size: 0.78rem;
   font-weight: 700;
}

.ms-count-badge--category {
   background: #fef3c7;
   color: #b45309;
}

.ms-count-badge--subcategory {
   background: #ecfdf5;
   color: #059669;
}

/* ── Action Buttons ── */
.ms-actions {
   text-align: right;
   white-space: nowrap;
}

.ms-action-btn {
   width: 36px;
   height: 36px;
   border-radius: 10px;
   border: none;
   cursor: pointer;
   font-size: 0.82rem;
   display: inline-flex;
   align-items: center;
   justify-content: center;
   margin-left: 6px;
   transition: transform 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease;
}

.ms-action-btn:hover {
   transform: translateY(-1px);
   box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.ms-action-btn--edit {
   background: #e6f7fa;
   color: #00A0C1;
}

.ms-action-btn--edit:hover {
   background: #00A0C1;
   color: #fff;
}

.ms-action-btn--delete {
   background: #fef2f2;
   color: #dc2626;
}

.ms-action-btn--delete:hover {
   background: #dc2626;
   color: #fff;
}

.ms-action-btn--disabled {
   opacity: 0.4;
   cursor: not-allowed;
}

.ms-action-btn--disabled:hover {
   transform: none;
   box-shadow: none;
   background: #fef2f2;
   color: #dc2626;
}

/* ── Subtable Row ── */
.ms-subtable-row {
   background: #f8fafc;
}

.ms-subtable-cell {
   padding: 0 16px 16px !important;
   border-bottom: none !important;
}

/* ── Empty State ── */
.ms-empty-row {
   text-align: center;
   padding: 48px 16px !important;
}

.ms-empty-state {
   display: flex;
   flex-direction: column;
   align-items: center;
   gap: 8px;
   color: #94a3b8;
}

.ms-empty-state i {
   font-size: 2rem;
   color: #cbd5e1;
}

.ms-empty-state span {
   font-size: 0.9rem;
}

/* ── Responsive ── */
@media (max-width: 768px) {
   .ms-summary-row {
      grid-template-columns: 1fr;
   }

   .ms-card__toolbar {
      flex-direction: column;
      align-items: stretch;
   }
}
</style>